<template>
    <span class="role-selector">
        <Select
            :placeholder="isFetching ? '...' : elementName"
            :loading="isFetching"
            :options="roles"
            :invalid="allLoadedButNoRole"
            :disabled="!isEditing"
            v-model="selectedRole"
            optionLabel="name"
            value="identity"
            @change="changeRole"
            variant="outlined"
            dropdownIcon="-"
            v-tooltip.top="
                'These roles can be selected from what you have defined at the domain level.'
            "
        >
            <template #footer>
                <div class="p-3" @click="handleAddRole">
                    <Button
                        label="Add role"
                        fluid
                        severity="secondary"
                        variant="text"
                        size="small"
                        icon="pi pi-plus"
                    />
                </div>
            </template>
        </Select>
        <i
            v-if="hasConflict"
            class="pi pi-exclamation-triangle"
            style="color: orange"
            v-tooltip="'This role is already assigned to another lane.'"
        />
    </span>
</template>

<script setup lang="ts">
import { type Element as BPMNElement } from "bpmn-js/lib/model/Types";
import { getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import type { SelectChangeEvent } from "primevue/select";
import { computed, ref, watch } from "vue";

import { useBpmnManager } from "@/composables/bpmn-manager";
import { matchLaneRole } from "@/composables/bpmn-manager/lane-role";
import { useEditManager } from "@/composables/edit-manager";
import { useEditRoleDialog } from "@/composables/edit-role-dialog";
import { useProcessManager } from "@/composables/process-manager";
import { Role } from "@/composables/role-manager";
import { useLanesManager } from "@/stores/lanes-store";
import { useRolesManager } from "@/stores/roles-store";

const props = defineProps<{
    element: BPMNElement;
    bpmnManager: ReturnType<typeof useBpmnManager>;
}>();

const { isEditing } = useEditManager();
const { lanes, isFetching: isFetchingLanes } = useLanesManager();
const { roles, isLoading: isLoadingRoles } = useRolesManager();
const { isFetching: isFetchingProcess } = useProcessManager();
const { open: openRoleDialog } = useEditRoleDialog();

const hasConflict = ref(false);

const selectedRole = ref<Role>();
const elementName = computed(
    () => props.element.name || props.element.businessObject?.name || "..."
);
const elementRoleId = ref();

watch(
    [() => props.element, props.bpmnManager.xml],
    () => {
        elementRoleId.value = getBusinessObject(props.element).roleId;
    },
    { immediate: true }
);

const lane = computed(() => {
    return lanes.value?.find((l) => {
        return l.bpmn_id === getBusinessObject(props.element).id;
    });
});

const isFetching = computed(() =>
    Boolean(
        isFetchingLanes.value || isFetchingProcess.value || isLoadingRoles.value
    )
);

watch([selectedRole, props.bpmnManager.xml, isFetching], async () => {
    if (isFetching.value) {
        return;
    }
    const bpmnLanes = (await props.bpmnManager.getLanes()).map(
        getBusinessObject
    );
    hasConflict.value = bpmnLanes.some(
        (l) =>
            l.id !== getBusinessObject(props.element).id &&
            l.roleId === selectedRole.value?.identity
    );
});

// Flag the selector as invalid once everything is loaded but no role resolves.
// Covers both a lane with no role at all and one whose roleId names a role this
// org doesn't have (e.g. an imported diagram) — the latter would otherwise sit
// blank with no marker, since the match above leaves selectedRole unset.
const allLoadedButNoRole = computed(() => {
    if (isFetching.value) return false;
    return Boolean(lane.value && roles.value && !selectedRole.value);
});

watch(
    [lane, () => roles.value, elementRoleId],
    ([newLane, currentRoles, roleId]) => {
        if (!currentRoles) return;

        // Prefer the diagram's roleId (the user's pick), then the backend's
        // role_id (which can lag a fresh edit), then a name match for legacy
        // lanes. Preferring the diagram stops a stale backend value from winning
        // the match and overwriting a just-picked role (see lane-role.ts).
        const role = matchLaneRole(
            currentRoles,
            roleId,
            newLane?.role_id,
            getBusinessObject(props.element).name
        );

        if (role) {
            selectedRole.value = role;
            // Persist the roleId on the BPMN element if it was matched by
            // name so future lookups can use the faster ID path.
            if (!roleId || roleId !== role.identity) {
                props.bpmnManager.updateElementProperties(props.element.id, {
                    name: role.name,
                    roleId: role.identity,
                });
            }
        }
    },
    { immediate: true }
);

const assignRoleToLane = (role: Role) => {
    selectedRole.value = role;
    props.bpmnManager.updateElementProperties(props.element.id, {
        name: role.name,
        roleId: role.identity,
    });
    // Make sure the change is picked up.
    props.bpmnManager.eventBus?.fire("commandStack.changed");
};

const handleAddRole = async () => {
    const createdRole = await openRoleDialog();
    if (!createdRole) {
        return;
    }

    assignRoleToLane(createdRole);
};

const changeRole = (event: SelectChangeEvent) => {
    const roleId = event.value.identity;
    if (!roleId) {
        return;
    }

    const role = roles.value?.find((item) => item.identity === roleId);
    if (!role) {
        return;
    }

    assignRoleToLane(role);
};
</script>

<style scoped lang="scss">
.role-selector {
    display: flex;
    align-items: center;
    position: absolute;
    top: 1px;
    left: 1px;

    :deep(.p-select) {
        background: none;
        border: none;
        box-shadow: none;

        .p-select-dropdown {
            width: 0;
        }

        &.p-invalid {
            .p-select-label,
            .p-select-dropdown {
                color: var(--p-select-invalid-border-color);
            }
        }
    }
}
</style>
